import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import { getRequest, postRequest } from "../../utils/api";
import { logout } from "../../utils/auth";
import Pill from "../../Component/Pill";
import AffiliateLayout, { TICKET_STATUS, affiliateName } from "./components/AffiliateLayout";

// Affiliate support tickets — list + conversation. Replies are emailed to the affiliate by the API.
const AffiliateTickets = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedId = searchParams.get("id");

    const [tickets, setTickets] = useState<any[]>([]);
    const [statusCounts, setStatusCounts] = useState({ all: 0, open: 0, answered: 0, closed: 0 });
    const [isLoader, setIsLoader] = useState<boolean>(true);
    const [statusFilter, setStatusFilter] = useState("0");
    const [searchText, setSearchText] = useState("");
    const [searchQuery, setSearchQuery] = useState("");

    const [detail, setDetail] = useState<any>(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [reply, setReply] = useState("");
    const [sending, setSending] = useState(false);
    const msgsEndRef = useRef<HTMLDivElement>(null);

    // Fetch Ticket List
    const getTickets = useCallback(async () => {
        try {
            setIsLoader(true);
            const response = await getRequest(
                `/affiliate-ticket/list?page=1&limit=100&search=${encodeURIComponent(searchQuery)}&status=${statusFilter}`
            );
            if (response.status === "success") {
                setTickets(response.data || []);
                if (response.statusCounts) setStatusCounts(response.statusCounts);
            } else if (response.status === "session_expired") {
                logout();
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            toast.error("Something went wrong loading tickets!");
        } finally {
            setIsLoader(false);
        }
    }, [searchQuery, statusFilter]);

    useEffect(() => {
        getTickets();
    }, [getTickets]);

    // Debounced Search
    useEffect(() => {
        const delay = setTimeout(() => setSearchQuery(searchText), 500);
        return () => clearTimeout(delay);
    }, [searchText]);

    // Fetch Ticket + Conversation
    useEffect(() => {
        setReply("");
        if (!selectedId) { setDetail(null); return; }
        const getDetail = async () => {
            try {
                setLoadingDetail(true);
                const response = await getRequest(`/affiliate-ticket/detail/${selectedId}`);
                if (response.status === "success") {
                    setDetail(response.data);
                } else if (response.status === "session_expired") {
                    logout();
                } else {
                    toast.error(response.message);
                    setDetail(null);
                }
            } catch (error) {
                toast.error("Something went wrong loading the ticket!");
            } finally {
                setLoadingDetail(false);
            }
        };
        getDetail();
    }, [selectedId]);

    // Keep the latest message in view
    useEffect(() => {
        msgsEndRef.current?.scrollIntoView({ block: "end" });
    }, [detail?.messages?.length]);

    const sendReply = async () => {
        const message = reply.trim();
        if (!message || !detail) return;
        if (message.length > 3000) return toast.warning("Message must be 3000 characters or less");

        try {
            setSending(true);
            const response = await postRequest(`/affiliate-ticket/reply/${detail.ticket._id}`, { message });
            if (response.status === "success") {
                toast.success(response.message);
                setReply("");
                setDetail((prev: any) => ({
                    ticket: { ...prev.ticket, at_status: response.data.at_status },
                    messages: [...prev.messages, response.data.message],
                }));
                getTickets();
            } else if (response.status === "session_expired") {
                logout();
            } else {
                toast.error(response.message);
            }
        } catch (error: any) {
            toast.error(typeof error === "string" ? error : "Something went wrong");
        } finally {
            setSending(false);
        }
    };

    const changeTicketStatus = async (status: number) => {
        if (!detail) return;
        try {
            const response = await postRequest(`/affiliate-ticket/status/${detail.ticket._id}`, { status });
            if (response.status === "success") {
                toast.success(response.message);
                setDetail((prev: any) => ({ ...prev, ticket: { ...prev.ticket, at_status: response.data.at_status } }));
                getTickets();
            } else if (response.status === "session_expired") {
                logout();
            } else {
                toast.error(response.message);
            }
        } catch (error: any) {
            toast.error(typeof error === "string" ? error : "Something went wrong");
        }
    };

    const chips: [string, string, number][] = [
        ["0", "Open", statusCounts.open],
        ["1", "Answered", statusCounts.answered],
        ["2", "Closed", statusCounts.closed],
        ["", "All", statusCounts.all],
    ];

    const t = detail?.ticket;
    const aff = t?.at_affiliate_id;

    return (
        <AffiliateLayout
            loading={isLoader && tickets.length === 0}
            stats={[
                { label: "All tickets", value: String(statusCounts.all), icon: "ri-stack-line", delta: "All time" },
                { label: "Open tickets", value: String(statusCounts.open), icon: "ri-inbox-line", delta: "Waiting for a reply" },
                { label: "Answered", value: String(statusCounts.answered), icon: "ri-reply-line", delta: "Waiting for the affiliate" },
                { label: "Closed", value: String(statusCounts.closed), icon: "ri-checkbox-circle-line", delta: "All time" },
            ]}
        >
            <div className="cx-card">
                <div className="cx-tk-layout">
                    <div className="cx-tk-list">
                        <div className="cx-toolbar">
                            <div className="cx-chips">
                                {chips.map(([k, l, n]) => (
                                    <button type="button" key={l} className={`cx-chip ${statusFilter === k ? "on" : ""}`} onClick={() => setStatusFilter(k)}>
                                        {l} <b>{(n || 0).toLocaleString("en-IN")}</b>
                                    </button>
                                ))}
                            </div>
                            <input
                                className="cx-input"
                                type="search"
                                style={{ width: "100%" }}
                                placeholder="Search ticket ID, subject, affiliate…"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                        </div>
                        {tickets.length ? tickets.map(item => {
                            const [tone, label] = TICKET_STATUS[item.at_status] || TICKET_STATUS[0];
                            return (
                                <div key={item._id} className={`cx-tk ${item._id === selectedId ? "on" : ""}`} onClick={() => setSearchParams({ id: item._id })}>
                                    <div className="l1">
                                        <span className="mono">#{item.at_unique_id} · {affiliateName(item.at_affiliate_id)}</span>
                                        <span>{moment(item.at_last_reply_at || item.createdAt).fromNow()}</span>
                                    </div>
                                    <div className="l2">{item.at_subject}</div>
                                    <div className="l3">
                                        <Pill tone={tone}>{label}</Pill> &nbsp;{item.at_category}
                                        {item.at_status === 0 && item.at_last_reply_by === "affiliate" && " · awaiting reply"}
                                    </div>
                                </div>
                            );
                        }) : (
                            <div className="cx-empty"><i className="ri-inbox-line"></i>{isLoader ? "Loading…" : "No tickets here."}</div>
                        )}
                    </div>

                    <div className="cx-tk-pane">
                        {!selectedId ? (
                            <div className="cx-empty" style={{ margin: "auto" }}><i className="ri-chat-3-line"></i>Select a ticket</div>
                        ) : loadingDetail && !t ? (
                            <div className="cx-empty" style={{ margin: "auto" }}><i className="ri-loader-4-line"></i>Loading ticket…</div>
                        ) : t && <>
                            <div className="cx-card-h">
                                <div style={{ minWidth: 0 }}>
                                    <h6>{t.at_subject}</h6>
                                    <div className="fs12 muted">
                                        <span className="mono">#{t.at_unique_id}</span> · {t.at_category} · {affiliateName(aff)}
                                        {aff?.affiliate_email ? ` · ${aff.affiliate_email}` : ""}
                                        {aff?.affiliate_mobile ? ` · ${`${aff.affiliate_country_code || ""} ${aff.affiliate_mobile}`.trim()}` : ""}
                                    </div>
                                </div>
                                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                                    <Pill tone={(TICKET_STATUS[t.at_status] || TICKET_STATUS[0])[0]}>{(TICKET_STATUS[t.at_status] || TICKET_STATUS[0])[1]}</Pill>
                                    {t.at_status === 2
                                        ? <button type="button" className="cx-btn cx-btn-light cx-btn-xs" onClick={() => changeTicketStatus(0)}><i className="ri-arrow-go-back-line"></i>Reopen</button>
                                        : <button type="button" className="cx-btn cx-btn-signal cx-btn-xs" onClick={() => changeTicketStatus(2)}><i className="ri-check-line"></i>Close</button>}
                                </div>
                            </div>

                            <div className="cx-tk-msgs">
                                {detail.messages.map((m: any) => (
                                    <div key={m._id} className={`cx-msg ${m.atm_sender === "admin" ? "me" : ""}`}>
                                        <b style={{ display: "block", fontSize: 11, opacity: .75 }}>{m.atm_sender === "admin" ? "UOMOX Support" : affiliateName(aff)}</b>
                                        <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{m.atm_message}</span>
                                        <small>{moment(m.createdAt).format("DD MMM, HH:mm")}</small>
                                    </div>
                                ))}
                                <div ref={msgsEndRef}></div>
                            </div>

                            <div className="cx-tk-reply">
                                <textarea
                                    className="cx-input"
                                    style={{ height: 60, padding: "8px 10px", resize: "none" }}
                                    placeholder={t.at_status === 2 ? "Replying will reopen this ticket…" : "Type a reply — it's emailed to the affiliate…"}
                                    value={reply}
                                    onChange={(e) => setReply(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                                ></textarea>
                                <button type="button" className="cx-btn cx-btn-primary" onClick={sendReply} disabled={sending || !reply.trim()} aria-label="Send">
                                    {sending ? "…" : <i className="ri-send-plane-2-fill"></i>}
                                </button>
                            </div>
                        </>}
                    </div>
                </div>
            </div>
        </AffiliateLayout>
    );
};

export default AffiliateTickets;
